/**
 * 연결 블록 — 품의·계약서를 한 자리에서 잇는다 (PORTAL-13739).
 * 설계: 260820_계약서 연결 화면설계서 v1.1
 *
 * 기존 연결품의(parents/children/related 3분류)를 종류 × 방향 두 축으로 바꾼 구조다.
 * 종류가 늘어도 화면이 길어지지 않게 입력은 드롭다운 둘 + 검색칸 하나로 통합한다.
 */

import type { ContractCardAccess, ContractTotalState } from './contractTypes';

/** 무엇을 붙이나 — 종류가 늘면 여기에 한 줄씩 추가된다(회의록 등 후속). */
export type LinkKind = 'approval' | 'contract';

/** 이 문서와 어떤 관계인가 — 종류와 무관하게 항상 이 셋. */
export type LinkDirection = 'preceding' | 'following' | 'related';

export const LINK_KIND_LABEL: Record<LinkKind, string> = {
  approval: '품의',
  contract: '계약서',
};

export const LINK_DIRECTION_LABEL: Record<LinkDirection, string> = {
  preceding: '선행',
  following: '후속',
  related: '관련',
};

/**
 * 드롭다운에 함께 뜨는 한 줄 설명 — 라벨만으로는 방향의 뜻이 서지 않는다.
 * 앞뒤 순서로 적지 않고 쓰임(근거·결과)으로 적는다 — 무엇을 골라야 하는지 바로 잡힌다.
 */
export const LINK_DIRECTION_HINT: Record<LinkDirection, string> = {
  preceding: '이 품의의 근거가 된 문서',
  following: '이 품의로 만들어진 문서',
  related: '근거도 결과도 아니지만 함께 볼 문서',
};

/**
 * e-law 계약 상태 8종. 매핑되지 않는 코드는 서버가 함께 내려주는 상태명을 그대로 쓴다.
 * 완료/반려만 결과 상태이고 나머지는 진행 단계다.
 */
export type ContractStatus =
  | 'drafting'
  | 'reviewing'
  | 'negotiating'
  | 'approvalPending'
  | 'signing'
  | 'collectPending'
  | 'completed'
  | 'rejected';

export const CONTRACT_STATUS_LABEL: Record<ContractStatus, string> = {
  drafting: '작성중',
  reviewing: '검토중',
  negotiating: '협의중',
  approvalPending: '승인대기',
  signing: '서명중',
  collectPending: '회수대기',
  completed: '완료',
  rejected: '반려',
};

/**
 * 포탈 백엔드가 중계하는 e-law 상태 코드(숫자 문자열) → 상태 8종.
 * dev 실측(2026-09-28)으로 확인한 코드만 적는다. 이름은 서버가 status_name 으로 함께 주므로
 * 여기서는 톤(진행·완료·반려)을 정하는 데만 쓴다. 표에 없는 코드(130 취소, 133·134 확인 필요)는 서버 이름을 회색으로 찍는다.
 */
export const CONTRACT_STATUS_BY_CODE: Record<string, ContractStatus> = {
  '100': 'signing', // 서명가능
  '104': 'collectPending', // 원본회수대기중
  '119': 'rejected', // 법무팀장최종반려
  '120': 'completed', // 완료
};

/**
 * 계약이 정한 금액 한 항목. 계약마다 산정 방식이 달라 값 하나로 요약할 수 없다 —
 * 총액·월 정액·요율·인원당 단가가 한 계약에 섞여 있기도 하다 (PORTAL-14182).
 *
 * value 를 문자열로 두는 이유: "집행액의 15%" 처럼 숫자가 아닌 값이 실제로 있다.
 */
export type ContractAmountItem = {
  /** 항목 이름 — "계약 총액", "월 고정 운영비", "대행 수수료" */
  name: string;
  /** 표시할 값 — "100,000,000원", "300,000원", "집행액의 15%" */
  value: string;
  /** 무엇을 기준으로 잡은 금액인지 — "월", "인당". 없을 수 있다. */
  basis?: string;
  /** 총액 항목인지. 카드 한 줄 요약에서 이 항목을 우선 고른다. */
  isTotal?: boolean;
};

/** 계약 카드 둘째 줄에 쓰는 값들. 프론트는 받은 값을 그대로 표시한다. */
export type ContractMeta = {
  /** 정규화·마스킹된 상대방명. 프론트가 개인 여부를 판정하지 않는다. */
  counterparty: string | null;
  /** 계약 기간 — 둘 중 하나라도 없거나 역전이면 표시하지 않는다. */
  startDate: string | null;
  endDate: string | null;
  /** 계약이 정한 금액 항목들. 빈 배열이면 totalState 로 "없음"과 "모름"을 가른다. */
  amounts: ContractAmountItem[];
  /**
   * 총액을 적을 수 있는 상태인지.
   * 'not_specified' 만 "총액 없음"으로 적고, 'unknown'·'extraction_failed' 는 자리를 비운다 —
   * 없는 것과 모르는 것이 같아 보이면 결재자가 판단할 수 없다.
   */
  totalState?: ContractTotalState | null;
  /** 새 탭으로 여는 계약 원문 주소. */
  externalUrl: string;
};

/** 연결 항목 하나 — 품의와 계약서가 같은 모양을 쓰고, 계약만 contract 를 더 갖는다. */
export type LinkItem = {
  id: string;
  kind: LinkKind;
  direction: LinkDirection;
  title: string;
  /**
   * 제목 앞에 적는 문서의 종류 이름 — 품의는 양식명(기본품의 등), 계약은 계약 유형.
   * 검색 결과에서 같은 이름의 문서를 가르는 첫 단서라 제목보다 앞에 온다.
   */
  typeName?: string;
  /** 품의는 기안자, 계약은 담당자 / 부서 */
  author: string;
  /** 품의는 상신일, 계약은 체결일 */
  date: string | null;
  /** 품의는 결재 상태 코드(P/H/C/R), 계약은 ContractStatus */
  status: string;
  /** 계약 상태가 8종 매핑에 없을 때 서버가 준 이름. 없으면 상태를 감춘다. */
  statusLabel?: string | null;
  /** 문서로 이동하는 주소 */
  href: string;
  contract?: ContractMeta;
  /**
   * 이 항목을 어디까지 볼 수 있는지.
   * 'full' 이 아니면(법틀 권한 없음·못 찾음·조회 장애) 연결 자체는 문서에 남아 있으므로
   * 카드는 보이되 안내 문장만 적는다.
   */
  access?: ContractCardAccess;
  /**
   * 볼 수 있는 계약인데 계약 플랫폼 값이 아직 없을 때의 사유.
   * 'analyzing'=수집·분석 전 / 'unavailable'=조회 장애. 계약명·새 탭 링크는 그대로 둔다.
   */
  metadataNote?: 'analyzing' | 'unavailable';
};
